import { ChangeDetectionStrategy, Component, HostListener, NgZone, OnInit } from '@angular/core';

import { TranslationService } from './modules/i18n';
// language list
import { locale as enLang } from './modules/i18n/vocabs/en';
import { locale as chLang } from './modules/i18n/vocabs/ch';
import { locale as esLang } from './modules/i18n/vocabs/es';
import { locale as jpLang } from './modules/i18n/vocabs/jp';
import { locale as deLang } from './modules/i18n/vocabs/de';
import { locale as frLang } from './modules/i18n/vocabs/fr';
import { ThemeModeService } from './_metronic/partials/layout/theme-mode-switcher/theme-mode.service';
import { RbacService } from './shared/services/rbac/rbac.service';
import { Router } from '@angular/router';
import { CognitoService } from './modules/auth/services/cognito.service';
import { Subscription, tap } from 'rxjs';
import { getDecodedToken } from './shared/utils/config/decode-token.util';
import { SweetAlertService } from './shared/services/sweet-alert/sweet-alert.service';
import { DateCommunicationService } from './shared/services/date-communication/date-communication.service';
import { getTodayDate } from './shared/utils/format/time.util';
import { ThemeConfig, ThemeService } from './shared/services/theme/theme.service';
import { Console } from 'console';

@Component({
  // tslint:disable-next-line:component-selector
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'body[root]',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private readonly INACTIVITY_LIMIT = 10 * 60 * 1000;
  private readonly TOKEN_REFRESH_LIMIT = 1 * 60 * 1000;
  private refreshTokenTimeout: any;
  private inactivityTimer: any;
  private warningTimer: any;
  private _token = localStorage.getItem('rbac-token');
  private eventListeners: (() => void)[] = [];
  private logoutSubscription!: Subscription;
  public startDate: string = getTodayDate();
  public endDate: string = getTodayDate();

  constructor(
    private translationService: TranslationService,
    private _cognitoService: CognitoService,
    private modeService: ThemeModeService,
    private _service: RbacService,
    private _router: Router,
    private _zone: NgZone,
    private _sweetAlertService: SweetAlertService,
    private _dateService: DateCommunicationService,
    private _themeService: ThemeService,
  ) {
    // register translations
    this.translationService.loadTranslations(
      enLang,
      chLang,
      esLang,
      jpLang,
      deLang,
      frLang
    );
    this.sendDateToChildren(this.startDate, this.endDate);
  }

  ngOnInit() {
    this.modeService.init();
    this._service.isAuthenticated$.subscribe((isAuthenticated) => {
      if (isAuthenticated || this._token!!) {
        this.startInactivityTimer();
      }
    })

    this.logoutSubscription = this._cognitoService.logout$.subscribe(() => {
      this.stopInactivityTimer();
      this.stopRefreshTokenTimer();
    });

    const themeConfig  = localStorage.getItem('themeConfig')
    if(themeConfig){
      this.applyThemeForConsortium('');
    }
  }

  private applyThemeForConsortium(name: string): void {
    this._themeService.loadTheme(name).subscribe((theme: ThemeConfig) => {
      this._themeService.applyTheme(theme);
    });
  }

  private startInactivityTimer(): void {
    this.resetInactivityTimer();
    this.refreshToken();

    const activityEvents = ['keydown', 'touchstart', 'click', 'scroll'];

    activityEvents.forEach(event => {
      const handler = () => {
        this.resetInactivityTimer();
      };
      window.addEventListener(event, handler);
      this.eventListeners.push(() => window.removeEventListener(event, handler));
    });
  }

  public refreshToken(): void {
    const { exp } = getDecodedToken();

    try {
      if (!exp) this.logoutUser();

      const expires = new Date(exp * 1000);
      const timeout = expires.getTime() - Date.now() - (this.TOKEN_REFRESH_LIMIT);

      this.refreshTokenTimeout = setTimeout(() => {
        this._cognitoService.refreshToken(this._cognitoService.getAccessToken()!)
          .pipe(
            tap(({ data }) => {
              this._cognitoService.saveTokens(data.accessToken, data.refreshToken);
              this.refreshToken();
            })
          ).subscribe();
      }, timeout);

    } catch (error) {
      this.logoutUser();
    }
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }

  private stopInactivityTimer(): void {
    this.eventListeners.forEach(removeListener => removeListener());
    this.eventListeners = [];
    clearTimeout(this.inactivityTimer);
    clearTimeout(this.warningTimer);
  }

  private resetInactivityTimer(): void {
    clearTimeout(this.inactivityTimer);
    clearTimeout(this.warningTimer);

    this._zone.runOutsideAngular(() => {
      this.warningTimer = setTimeout(() => {
        this._sweetAlertService.mixin('Advertencia: Inactividad detectada. La sesión está por expirar.', 'warning');
      }, this.INACTIVITY_LIMIT - (60 * 1000));

      this.inactivityTimer = setTimeout(() => {
        this.logoutUser();
      }, this.INACTIVITY_LIMIT);
    });
  }

  private logoutUser(): void {
    this.stopInactivityTimer();
    this._cognitoService.signOut();
  }

  public sendDateToChildren(startDate: string, endDate: string): void {
    this._dateService.updateDateRange(startDate, endDate);
  }

  ngOnDestroy(): void {
    if (this.logoutSubscription) {
      this.logoutSubscription.unsubscribe();
    }
    this.stopInactivityTimer();
  }
}
